import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductStatusType } from "@/types/graphql/partner.generated";

export type ProductStatusFieldValue = ProductStatusType | null;

interface ProductStatusOption {
  value: ProductStatusFieldValue;
  label: string;
}

interface ProductStatusFieldSingleProps {
  multiple?: false;
  value: ProductStatusFieldValue;
  onChange: (value: ProductStatusFieldValue) => void;
  label?: string;
  variant?: "outlined" | "standard" | "filled";
}

interface ProductStatusFieldMultipleProps {
  multiple: true;
  value: ProductStatusFieldValue[];
  onChange: (value: ProductStatusFieldValue[]) => void;
  label?: string;
  variant?: "outlined" | "standard" | "filled";
}

type ProductStatusFieldProps =
  | ProductStatusFieldSingleProps
  | ProductStatusFieldMultipleProps;

/**
 * Селект статуса продукта (референс: components/ProductStatusField).
 * null — «не завершённые» (дефолт списка, не enum-значение бэкенда), ALL —
 * значение фильтра «все статусы» (backend ProductStatus.ALL, реальному
 * полю Product.status не присваивается).
 */
export const ProductStatusField: FC<ProductStatusFieldProps> = ({
  multiple,
  value,
  onChange,
  label,
  variant,
}) => {
  const { t } = useTranslation();

  const options: ProductStatusOption[] = [
    { value: null, label: t("products.status.notFinished") },
    { value: ProductStatusType.AllType, label: t("products.status.all") },
    {
      value: ProductStatusType.PublishedType,
      label: t("products.status.published"),
    },
    { value: ProductStatusType.DraftType, label: t("products.status.draft") },
    {
      value: ProductStatusType.FinishedType,
      label: t("products.status.finished"),
    },
  ];

  if (multiple) {
    const selected = options.filter((option) => value.includes(option.value));

    return (
      <Autocomplete
        multiple
        fullWidth
        filterSelectedOptions
        options={options}
        value={selected}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) =>
          option.value === current.value
        }
        onChange={(_event, newValue) =>
          onChange(newValue.map((option) => option.value))
        }
        renderInput={(params) => (
          <TextField {...params} variant={variant} label={label} />
        )}
      />
    );
  }

  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      onChange={(_event, newValue) => onChange(newValue?.value ?? null)}
      renderInput={(params) => (
        <TextField {...params} variant={variant} label={label} />
      )}
    />
  );
};
